import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ActivateRecordComposerDefinitionDocument,
  type ActivateRecordComposerDefinitionMutation,
  type ActivateRecordComposerDefinitionMutationVariables,
  DeactivateRecordComposerDefinitionDocument,
  type DeactivateRecordComposerDefinitionMutation,
  type DeactivateRecordComposerDefinitionMutationVariables,
  GetRecordComposerDefinitionDetailDocument,
  GetRecordComposerDefinitionsDocument,
  type GetRecordComposerDefinitionsQueryVariables,
} from "~/common/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type Composer = {
  id: string;
  name: string;
};

type UseRelationComposerActivationOptions = {
  listVariables?: Omit<GetRecordComposerDefinitionsQueryVariables, "tenantId">;
  canExecute?: () => boolean;
};

export const useRelationComposerActivation = ({ listVariables, canExecute }: UseRelationComposerActivationOptions) => {
  const { t } = useTranslation();
  const handleActionWithConfirm = useActionWithConfirm();
  const { tenantId } = useTenant();
  const [deactivateRelationComposerDef] = useMutation<
    DeactivateRecordComposerDefinitionMutation,
    DeactivateRecordComposerDefinitionMutationVariables
  >(DeactivateRecordComposerDefinitionDocument);

  const [activateRecordComposerDef] = useMutation<
    ActivateRecordComposerDefinitionMutation,
    ActivateRecordComposerDefinitionMutationVariables
  >(ActivateRecordComposerDefinitionDocument);

  const onDeactivate = useCallback(
    (composer: Composer) => {
      if (!tenantId) {
        toast.error(translateError(t, new Error("Tenant is not resolved")));
        return;
      }

      const refetchQueries = [
        { query: GetRecordComposerDefinitionDetailDocument, variables: { tenantId, id: composer.id } },
        ...(listVariables
          ? [{ query: GetRecordComposerDefinitionsDocument, variables: { tenantId, ...listVariables } }]
          : []),
      ];
      handleActionWithConfirm({
        confirmMessage: t("ui.deactivateX", { x: composer.name }),
        successMessage: t("feature.common.deactivatedSuccessfully", { model: "relationComposer" }),
        action: () =>
          deactivateRelationComposerDef({
            refetchQueries,
            variables: { input: { tenantId, id: composer.id } },
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, deactivateRelationComposerDef, tenantId, listVariables, canExecute],
  );

  const onActivate = useCallback(
    (composer: Composer) => {
      if (!tenantId) {
        toast.error(translateError(t, new Error("Tenant is not resolved")));
        return;
      }

      const refetchQueries = [
        { query: GetRecordComposerDefinitionDetailDocument, variables: { tenantId, id: composer.id } },
        ...(listVariables
          ? [{ query: GetRecordComposerDefinitionsDocument, variables: { tenantId, ...listVariables } }]
          : []),
      ];
      handleActionWithConfirm({
        confirmMessage: t("ui.activateX", { x: composer.name }),
        successMessage: t("feature.common.activatedSuccessfully", { model: "relationComposer" }),
        action: () =>
          activateRecordComposerDef({
            refetchQueries,
            variables: { input: { tenantId, id: composer.id } },
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, activateRecordComposerDef, tenantId, listVariables, canExecute],
  );

  return {
    onActivate,
    onDeactivate,
  };
};
